import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toEmbeddableMediaUrl } from '../../../utils/mediaEmbeds';

const PUSHER_KEY = process.env.REACT_APP_PUSHER_KEY || '';
const PUSHER_CLUSTER = process.env.REACT_APP_PUSHER_CLUSTER || 'mt1';
const PUSHER_HOST = process.env.REACT_APP_PUSHER_HOST;
const PUSHER_PORT = process.env.REACT_APP_PUSHER_PORT;
const PUSHER_SCHEME = process.env.REACT_APP_PUSHER_SCHEME || 'https';
const PLAYBACK_CHANNEL = process.env.REACT_APP_PUSHER_PLAYBACK_CHANNEL || 'tv.playback';

const formatDateTime = (value) =>
  value
    ? new Date(value).toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' })
    : '—';

const PlaybackMonitor = () => {
  const [state, setState] = useState({ current: null, upcoming: [] });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/tv/playback-state');
      setState({ current: data.current ?? null, upcoming: data.upcoming ?? [] });
      setError('');
    } catch (err) {
      setError(err.response?.data?.message ?? 'Unable to load playback state.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 20_000);
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    if (!PUSHER_KEY || typeof window === 'undefined' || !window.Pusher) {
      return undefined;
    }

    const options = {
      cluster: PUSHER_CLUSTER,
      forceTLS: PUSHER_SCHEME !== 'http',
    };

    if (PUSHER_HOST) options.wsHost = PUSHER_HOST;
    if (PUSHER_PORT) options.wsPort = Number(PUSHER_PORT);
    if (PUSHER_SCHEME) options.wsScheme = PUSHER_SCHEME;

    const pusher = new window.Pusher(PUSHER_KEY, options);
    const channel = pusher.subscribe(PLAYBACK_CHANNEL);
    channel.bind('ProgramPlaybackUpdated', load);

    return () => {
      channel.unbind('ProgramPlaybackUpdated', load);
      pusher.unsubscribe(PLAYBACK_CHANNEL);
      pusher.disconnect();
    };
  }, []);

  const current = state.current;
  const playbackProgram = current?.program ?? null;
  const playbackMedia = current?.media ?? playbackProgram ?? {};
  const mediaUrl = playbackMedia?.source_url ?? playbackProgram?.source_url ?? '';
  const mediaType =
    playbackProgram?.media_type ??
    (current?.source === 'archive' ? 'video' : null);
  const thumbnailUrl = playbackMedia?.thumbnail_url ?? playbackProgram?.thumbnail_url;
  const isLikelyVideoUrl = Boolean(mediaUrl) && /\.(mp4|m4v|webm|mov)(\?|#|$)/i.test(mediaUrl);
  const embedUrl = toEmbeddableMediaUrl(mediaUrl);
  const isVideo =
    Boolean(mediaUrl) &&
    !embedUrl &&
    (mediaType === 'video' || current?.source === 'archive' || isLikelyVideoUrl);
  const isEmbeddable =
    Boolean(embedUrl) ||
    (Boolean(mediaUrl) && ['web', 'playlist'].includes(mediaType ?? ''));
  const status = useMemo(() => {
    if (loading) return 'Syncing…';
    if (!current) return 'Off-air';
    return current.status === 'in_progress' ? 'On air' : 'Queued';
  }, [current, loading]);

  return (
    <section style={{ display: 'grid', gap: '1.25rem' }}>
      <div className="card" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <p className="text-muted" style={{ marginBottom: '0.2rem' }}>
            Monitor what viewers see
          </p>
          <h2 style={{ margin: 0 }}>Playback monitor</h2>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <Link className="btn btn-muted" to="/tv" target="_blank" rel="noreferrer">
            Open viewer page
          </Link>
          <button type="button" className="btn btn-primary" onClick={load}>
            Refresh
          </button>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="card" style={{ display: 'grid', gap: '1rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '1rem' }}>
          <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', background: '#0a0a0f' }}>
            {isVideo ? (
              <video
                key={mediaUrl}
                src={mediaUrl}
                poster={thumbnailUrl}
                style={{ width: '100%', height: '100%', maxHeight: 420, background: '#000' }}
                controls
                muted
                playsInline
                autoPlay
              />
            ) : isEmbeddable ? (
              <iframe
                key={embedUrl ?? mediaUrl}
                src={embedUrl ?? mediaUrl}
                title={current?.title ?? playbackProgram?.title ?? 'Embedded program'}
                style={{ width: '100%', height: '100%', minHeight: 280, background: '#000' }}
                frameBorder="0"
                allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <div
                style={{
                  minHeight: 280,
                  display: 'grid',
                  placeItems: 'center',
                  background: 'var(--bg-soft)',
                  color: 'var(--text-muted)',
                }}
              >
                <div style={{ textAlign: 'center' }}>
                  <p style={{ margin: 0 }}>No direct video preview</p>
                  <p style={{ margin: 0, fontSize: '0.95rem' }}>
                    {mediaUrl ? 'Source is not embeddable in preview' : 'Waiting for next slot'}
                  </p>
                </div>
              </div>
            )}
            <div className={`status-pill ${current ? 'live' : 'idle'}`} style={{ position: 'absolute', top: 12, left: 12 }}>
              {status}
            </div>
          </div>

          <div style={{ display: 'grid', gap: '0.75rem', alignContent: 'start' }}>
            <div>
              <p className="text-muted" style={{ marginBottom: '0.2rem' }}>
                {current?.type === 'live' ? 'Live window' : 'Program'}
              </p>
              <h3 style={{ margin: 0 }}>{current?.title ?? playbackProgram?.title ?? 'Off-air'}</h3>
              <p style={{ margin: '0.3rem 0 0', color: 'var(--text-muted)' }}>
                {playbackProgram?.description || current?.description || 'Monitoring the on-air feed in real time.'}
              </p>
            </div>

            <div className="pill-row">
              <span className="pill">Starts {formatDateTime(current?.starts_at)}</span>
              <span className="pill">Ends {formatDateTime(current?.ends_at)}</span>
              <span className="pill">{mediaType ?? current?.type ?? '—'}</span>
            </div>

            {mediaUrl && (
              <div className="field">
                <label>Source</label>
                <code className="code-inline" style={{ wordBreak: 'break-all' }}>
                  {mediaUrl}
                </code>
              </div>
            )}
          </div>
        </div>

        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <h4 style={{ margin: 0 }}>Upcoming</h4>
            <span className="text-muted">Auto-refreshing</span>
          </div>
          {state.upcoming?.length ? (
            <div className="timeline-grid">
              {state.upcoming.map((item) => (
                <div key={item.id} className="timeline-card">
                  <p className="text-muted" style={{ margin: 0 }}>
                    {item.type === 'live' ? 'Live' : 'Program'} · {formatDateTime(item.starts_at)}
                  </p>
                  <strong>{item.program?.title ?? item.title ?? 'Program'}</strong>
                  <p className="text-muted" style={{ margin: '0.2rem 0 0' }}>
                    {item.program?.media_type ?? item.type ?? '—'}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-muted">No upcoming items in the current window.</p>
          )}
        </div>
      </div>
    </section>
  );
};

export default PlaybackMonitor;
